/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-07T08:44:05.530Z
 * 소스 파일: src/data/dto/admin/doctor-list-response-dto
 */

import type { DoctorListResponseDto } from "@/data/dto/admin/doctor-list-response-dto";

/**
 * DoctorListResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isDoctorListResponseDto(input: unknown): input is DoctorListResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.uuid === "string" &&
    typeof obj.name === "string" && obj.name.trim() !== "" &&
    typeof obj.hospital === "string" &&
    typeof obj.treatmentCount === "number" && !isNaN(obj.treatmentCount) &&
    typeof obj.isDeactivated === "boolean"
  );
}

/**
 * DoctorListResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateDoctorListResponseDto(input: unknown): IValidation<DoctorListResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // uuid (required)
      if (objObj.uuid === undefined) {
        errors.push({ path: "uuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.uuid !== "string") {
          errors.push({ path: "uuid", expected: "string", value: objObj.uuid });
        }
      }
      // name (required)
      if (objObj.name === undefined) {
        errors.push({ path: "name", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.name === "string" && objObj.name.trim() !== "")) {
          errors.push({ path: "name", expected: "string (non-empty)", value: objObj.name });
        }
      }
      // hospital (required)
      if (objObj.hospital === undefined) {
        errors.push({ path: "hospital", expected: "string", value: undefined });
      } else {
        if (typeof objObj.hospital !== "string") {
          errors.push({ path: "hospital", expected: "string", value: objObj.hospital });
        }
      }
      // treatmentCount (required)
      if (objObj.treatmentCount === undefined) {
        errors.push({ path: "treatmentCount", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.treatmentCount === "number" && !isNaN(objObj.treatmentCount))) {
          errors.push({ path: "treatmentCount", expected: "number", value: objObj.treatmentCount });
        }
      }
      // isDeactivated (required)
      if (objObj.isDeactivated === undefined) {
        errors.push({ path: "isDeactivated", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.isDeactivated !== "boolean") {
          errors.push({ path: "isDeactivated", expected: "boolean", value: objObj.isDeactivated });
        }
      }
    }
  
  if (errors.length === 0 && isDoctorListResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "DoctorListResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * DoctorListResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertDoctorListResponseDto(input: unknown): DoctorListResponseDto {
  const validation = validateDoctorListResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertDoctorListResponseDto] :: 검증 실패: DoctorListResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
